/**
 * Public KB reader at /kb/public/:token — for POC emails.
 *
 * - No auth required (token in URL is the auth)
 * - 30-day signed JWT (kb_read intent) issued by backend.routers.kb
 * - Renders article body HTML; image src already rewritten by backend to
 *   include ?token=... so they work in this no-auth context.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen, Clock, Eye, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const RAW_BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${RAW_BASE.replace(/\/$/, "")}/api`;

export default function KbPublic() {
  const { token } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/kb/public/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          let msg = "This link is no longer valid.";
          try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch { /* */ }
          setError(msg);
          return null;
        }
        return r.json();
      })
      .then((d) => { if (d) setArticle(d); })
      .catch(() => setError("Unable to load this article."))
      .finally(() => setLoading(false));
  }, [token]);

  // Backend already rewrote /api/kb-files/<id> -> /api/kb-files/<id>?token=...
  // We just need to prefix with the absolute API host so the public page works
  // when opened outside the Kippu host (the email link is absolute too).
  const html = useMemo(() => {
    if (!article?.body_html) return "";
    return article.body_html.replace(
      /<img\s+([^>]*?)src=["']\/api\/kb-files\/([\w-]+)\?token=([^"']+)["']/g,
      (_m, attrs, fid, tk) => `<img ${attrs}src="${API}/kb-files/${fid}?token=${tk}"`,
    );
  }, [article]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Loading article…</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-6" data-testid="kb-public-error">
        <Card className="max-w-md border-slate-200">
          <CardContent className="p-8 text-center space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-rose-600 font-semibold">Link unavailable</div>
            <div className="text-slate-700 text-sm">{error}</div>
            <div className="text-[11px] text-slate-400">If you need access, contact the agent who shared this link.</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10" data-testid="kb-public-page">
      <div className="max-w-3xl mx-auto px-6 space-y-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500">
          <BookOpen className="w-3.5 h-3.5 text-indigo-600" /> Kippu Knowledge Base
        </div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900" data-testid="kb-public-title">
          {article.title}
        </h1>
        {article.summary && (
          <p className="text-sm text-slate-600">{article.summary}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          {article.category && (
            <span className="inline-flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> {article.category}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> Updated {article.updated_at ? new Date(article.updated_at).toLocaleDateString() : "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye className="w-3 h-3" /> {article.view_count || 0}
          </span>
          {(article.tags || []).length > 0 && (
            <span className="inline-flex items-center gap-1 flex-wrap">
              <Tag className="w-3 h-3" />
              {(article.tags || []).slice(0, 6).map((t) => (
                <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{t}</Badge>
              ))}
            </span>
          )}
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div
              className="prose prose-sm max-w-none kb-article-body"
              dangerouslySetInnerHTML={{ __html: html }}
              data-testid="kb-public-body"
            />
          </CardContent>
        </Card>

        <div className="text-[10px] text-slate-400 text-center pt-2">
          Read-only view · access expires automatically · © 2026 Zefficacy
        </div>
      </div>
    </div>
  );
}
